import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getCreditNotes, createCreditNote, getInvoicesPaged } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, CreditCard, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CreditNotes = () => {
  const { user } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    invoice_id: '',
    reason: '',
    amount: 0,
    notes: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [creditNotesData, invoicesData] = await Promise.all([
        getCreditNotes(),
        getInvoicesPaged({}, 1, 200)
      ]);
      setCreditNotes(creditNotesData);
      setInvoices(invoicesData.invoices || invoicesData);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createCreditNote(formData, user.id);
      toast.success('Nota de crédito creada');
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear nota de crédito');
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_id: '',
      reason: '',
      amount: 0,
      notes: ''
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="credit-notes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Notas de Crédito</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20"
              data-testid="add-credit-note-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nueva Nota de Crédito
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Nueva Nota de Crédito</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Factura *</Label>
                <Select value={formData.invoice_id} onValueChange={(value) => setFormData({ ...formData, invoice_id: value })}>
                  <SelectTrigger data-testid="credit-note-invoice-select">
                    <SelectValue placeholder="Seleccionar factura" />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.map(invoice => (
                      <SelectItem key={invoice.id} value={invoice.id}>
                        {invoice.invoice_number} - ${invoice.total.toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Razón *</Label>
                <Input
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Devolución, error en factura, etc."
                  required
                  data-testid="credit-note-reason-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Monto *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  required
                  data-testid="credit-note-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notas</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="credit-note-notes-input"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1" data-testid="save-credit-note-button">
                  Crear Nota de Crédito
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-credit-note-button">
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4" data-testid="credit-notes-list">
        {creditNotes.map(note => (
          <Card key={note.id} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`credit-note-card-${note.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                    <CreditCard className="w-5 h-5 mr-2 text-rose-500" />
                    {note.credit_note_number}
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1 mono-font">NCF: {note.ncf}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-rose-600 mono-font">${note.amount.toFixed(2)}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Factura Relacionada</p>
                  <p className="font-medium">{note.invoice_id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fecha</p>
                  <p className="font-medium">{note.created_at}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-600">Razón</p>
                <p className="font-medium">{note.reason}</p>
              </div>
              {note.notes && (
                <div className="bg-slate-50 p-3 rounded">
                  <p className="text-sm text-slate-600">Notas</p>
                  <p className="text-sm">{note.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {creditNotes.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            No hay notas de crédito registradas
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditNotes;